import type { Translate } from '@application/types';
import { useRouter } from '@application/utils';
import type { MtNotificationsGetNotificationsQuery } from '@gql/graphql';
import { NotificationType } from '@gql/graphql';
import type { MtNotificationItemData, NotificationRouteOptions } from '@presentation/components';
import dayjs from 'dayjs';
import { useMemo } from 'react';

export type MtNotificationsGetNotificationsType = NonNullable<
  MtNotificationsGetNotificationsQuery['findManyNotification']['items'][number]
>;

const mapNotificationTypeToUrl = (
  notification: MtNotificationsGetNotificationsType,
): NotificationRouteOptions => {
  const { type, applicationId, account, litterId, breeder } = notification;

  const applicationDetailsTypes = [
    NotificationType.BREEDER_APPLICATION_CREATED,
    NotificationType.BREEDER_APPLICATION_REFUSED,
    NotificationType.BREEDER_ON_APPLICATION_AWAITING,
    NotificationType.BUYER_APPLICATION_ADOPTED,
    NotificationType.BUYER_APPLICATION_REFUNDED,
    NotificationType.BREEDER_APPLICATION_ADOPTED,
    NotificationType.BUYER_APPLICATION_WAITLISTED,
    NotificationType.BUYER_APPLICATION_PAID,
  ];
  const applicationsListTypes = [
    NotificationType.BREEDER_APPLICATION_CANCELED,
    NotificationType.BREEDER_ON_APPLICATION_EXPIRED,
    NotificationType.BUYER_APPLICATION_REFUNDED,
    NotificationType.BUYER_APPLICATION_EMAIL_CONFIRM,
  ];
  const litterDetailsTypes = [
    NotificationType.BREEDER_LITTER_SOON_AVAILABLE,
    NotificationType.BREEDER_LITTER_ARCHIVED,
  ];
  const messagesListTypes = [NotificationType.MESSAGE_RECEIVED];
  const bookingsListTypes = [
    NotificationType.USER_BOOKING_EMAIL_CONFIRM,
    NotificationType.USER_BOOKING_SMS_REMINDER,
    NotificationType.USER_BOOKING_EMAIL_REMINDER,
    NotificationType.PRO_BOOKING_EMAIL_CONFIRM,
  ];

  const landingPageTypes = [NotificationType.BREEDER_NEW_ANNOUNCEMENT];

  const breederPageTypes = [NotificationType.BREEDER_REVIEW_RECEIVED];

  if (applicationsListTypes.includes(type)) {
    return { route: 'mtApplications' };
  }

  if (applicationDetailsTypes.includes(type)) {
    return { route: 'mtApplications', options: { params: [applicationId?.toString() ?? ''] } };
  }

  if (litterDetailsTypes.includes(type)) {
    return { route: 'mtLitters', options: { params: [litterId?.toString() ?? ''] } };
  }

  if (messagesListTypes.includes(type)) {
    return { route: 'mtMessages' };
  }

  if (bookingsListTypes.includes(type)) {
    return { route: 'mtAppointments' };
  }
  if (landingPageTypes.includes(type)) {
    return { route: 'mtLanding' };
  }
  if (breederPageTypes.includes(type)) {
    const breederUrl: string | undefined | null =
      breeder?.translations.find((tr) => tr.language === account?.language)?.url ??
      breeder?.translations[0].url;
    if (!breederUrl) {
      return { route: 'breederList' };
    }
    return {
      route: 'breeder',
      options: { params: [breederUrl], state: { scrollToReviews: true } },
    };
  }

  return { route: 'mtLanding' };
};

export const mapNotificationTypeToContent: (
  notification: MtNotificationsGetNotificationsType,
  translate: Translate,
) => string = (notification, translate) => {
  const buyerName =
    notification.application?.account?.name ??
    `${notification.application?.account?.firstName} ${notification.application?.account?.lastName}`;

  const applicationBreed =
    notification.application?.breedPreference?.translations.find(
      (tr) => tr.language === notification.application?.account.language,
    )?.name ?? notification.application?.breedPreference?.translations[0].name;

  return translate(`mt.notifications.content.${notification.type}`)
    .replace('$userName', buyerName ?? '')
    .replace('$applicationBreed', applicationBreed ?? '');
};

export const useFormatNotifications = (params: {
  notifications?: MtNotificationsGetNotificationsType[];
  unreadMessagesCount?: number;
}) => {
  const { notifications, unreadMessagesCount } = params;
  const { translate } = useRouter();

  const formattedNotifications: MtNotificationItemData[] | undefined = useMemo(() => {
    if (!notifications) {
      return;
    }

    const items = notifications.map((notification) => {
      const content = mapNotificationTypeToContent(notification, translate);

      return {
        id: notification.id,
        content,
        date: dayjs(notification.createdAt)
          .calendar(dayjs(), {
            sameDay: 'HH:mm',
            lastDay: `[${translate('glossary.yesterday')}] HH:mm`,
            nextDay: 'DD/MM/YYYY HH:mm',
            lastWeek: 'DD/MM/YYYY HH:mm',
            nextWeek: 'DD/MM/YYYY HH:mm',
            sameElse: 'DD/MM/YYYY HH:mm',
          })
          .toString(),
        success: notification.success,
        routeOptions: mapNotificationTypeToUrl(notification),
      } as MtNotificationItemData;
    });

    if (unreadMessagesCount) {
      const messagesNotifications = {
        id: 0,
        content: translate(`mt.notifications.content.${NotificationType.MESSAGE_RECEIVED}`).replace(
          '$count',
          String(unreadMessagesCount),
        ),
        success: false,
        routeOptions: { route: 'mtMessages' },
      } as MtNotificationItemData;

      return [messagesNotifications, ...items];
    }

    return items;
  }, [notifications, unreadMessagesCount, translate]);

  return { formattedNotifications };
};
